import { Controller } from '@hotwired/stimulus';
import consumer from '../channels/consumer';
import { getCsrfToken } from '../lib/csrf';

const MIN_LOADER_MS = 3000;
const SUBSCRIBE_TIMEOUT_MS = 5000;

export default class extends Controller {
  static targets = ['button', 'status', 'loadingTemplate', 'errorTemplate'];
  static values = {
    exportUrl: String,
  };

  async export(event) {
    event.preventDefault();
    this.startedAt = Date.now();
    this.#showLoading();

    try {
      await this.#subscribe();

      const response = await fetch(this.exportUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': getCsrfToken(),
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      });

      if (response.status === 429) {
        const data = await response.json();
        this.#showError(data.error);
        this.#unsubscribe();
      } else if (!response.ok) {
        this.#showError();
        this.#unsubscribe();
      }
    } catch {
      this.#showError();
      this.#unsubscribe();
    }
  }

  disconnect() {
    this.#unsubscribe();
  }

  #subscribe() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('ActionCable subscription timeout'));
      }, SUBSCRIBE_TIMEOUT_MS);

      this.subscription = consumer.subscriptions.create(
        'OperationsExportChannel',
        {
          connected: () => {
            clearTimeout(timeout);
            resolve();
          },
          received: (data) => this.#handleMessage(data),
        }
      );
    });
  }

  #unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  async #handleMessage(data) {
    const elapsed = Date.now() - this.startedAt;
    const remaining = MIN_LOADER_MS - elapsed;

    if (remaining > 0) {
      await new Promise((r) => setTimeout(r, remaining));
    }

    if (data.status === 'success') {
      this.#triggerDownload(data.csv);
    } else {
      this.#showError();
    }

    this.#unsubscribe();
  }

  #showLoading() {
    this.buttonTarget.classList.add('d-none');
    this.statusTarget.classList.remove('d-none');
    this.statusTarget.replaceChildren();
    this.statusTarget.appendChild(
      this.loadingTemplateTarget.content.cloneNode(true)
    );
  }

  async #triggerDownload(base64Csv) {
    this.#resetUI();

    const response = await fetch(
      `data:text/csv;charset=utf-8;base64,${base64Csv}`
    );
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const date = new Date().toISOString().slice(0, 10);

    const link = document.createElement('a');
    link.href = url;
    link.download = `transactions-${date}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  #resetUI() {
    this.buttonTarget.classList.remove('d-none');
    this.statusTarget.classList.add('d-none');
    this.statusTarget.replaceChildren();
  }

  #showError(message) {
    this.#resetUI();

    const node = this.errorTemplateTarget.content.cloneNode(true);
    if (message) {
      node.querySelector('[data-error-text]').textContent = message;
    }
    this.element.appendChild(node);
    setTimeout(() => {
      const el = this.element.querySelector('.export-error-toast');
      if (el) el.remove();
    }, 5000);
  }
}
